import axios from "axios";
import { env } from "../utils/env";
import { useAuth } from "../context/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ROUTE } from "../routes";

export type Scope = "all" | "individual" | "entity";

export type RiskLevel = "Low" | "Medium" | "High";

export interface Sector {
  id: number;
  name: string;
  risk_level: RiskLevel;
  active: boolean;
  updated_at: string;
  order: number | null;
  _type: string;
}

interface SectorResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Sector[];
}

const fetch = (authHeader: string, scope: Scope, activeOnly: boolean) => () => {
  const base = `${env.verifyServiceUrl}individual/business_sector_risk_level/`;
  const url =
    scope === "all" ? `${base}?all=true` : `${base}?_type=${scope}&all=true`;

  return axios.get<SectorResponse | Sector[]>(
    activeOnly ? `${url}&active=true` : url,
    { headers: { Authorization: authHeader } },
  );
};

export const useGetSectors = (scope: Scope = "all", activeOnly = false) => {
  const { authHeader } = useAuth();
  const navigate = useNavigate();

  return useQuery({
    queryKey: ["business_sector_risk_level", scope, activeOnly],
    queryFn: fetch(authHeader, scope, activeOnly),
    retry: false,
    select: (response): Sector[] => {
      const data = response.data as SectorResponse | Sector[];
      return Array.isArray(data) ? data : data.results ?? [];
    },
    onError: (err: any) => {
      const status = err?.response?.status ?? err?.request?.status;
      if (status === 500 || status === 503) navigate(ROUTE.SERVER_ERROR);
      if (status === 401) navigate(ROUTE.LOGOUT);
    },
  });
};
